import React, {
  forwardRef,
  type SelectHTMLAttributes,
  useEffect,
  useState,
} from "react"

import type { SelectItem } from "./Select"
import { SelectOptions } from "./SelectOptions"

// ---------------------------------------------------------------------------
// base-select feature detection (cached)
// ---------------------------------------------------------------------------

let _supportsBaseSelect: boolean | null = null

function supportsBaseSelect(): boolean {
  if (_supportsBaseSelect === null) {
    _supportsBaseSelect =
      typeof CSS !== "undefined" &&
      typeof CSS.supports === "function" &&
      CSS.supports("appearance", "base-select")
  }
  return _supportsBaseSelect
}

// ---------------------------------------------------------------------------
// JSX augmentation for <selectedcontent>
// ---------------------------------------------------------------------------

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      selectedcontent: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >
    }
  }
}

// ---------------------------------------------------------------------------
// SelectNative
// ---------------------------------------------------------------------------

export interface SelectNativeProps {
  /** Triggers invalid state (maps to aria-invalid). */
  invalid?: boolean
  /** Options to render inside the select. */
  options: SelectItem[]
  /** Placeholder text rendered as a disabled hidden option. `null` suppresses the option. */
  placeholder: string | null
}

export type SelectNativeElementProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  keyof SelectNativeProps | "multiple"
> &
  SelectNativeProps

export const SelectNative = forwardRef<
  HTMLSelectElement,
  SelectNativeElementProps
>(function SelectNative(
  { invalid, options, placeholder, ...restProps }: SelectNativeElementProps,
  ref
) {
  // Keep first render deterministic for SSR/hydration, then detect support on mount.
  const [isCustom, setIsCustom] = useState(false)

  useEffect(() => {
    if (supportsBaseSelect()) {
      setIsCustom(true)
    }
  }, [])

  return (
    <select
      {...restProps}
      aria-invalid={invalid ? "true" : undefined}
      ref={ref}
    >
      {isCustom && (
        <button>
          <selectedcontent />
        </button>
      )}
      {placeholder && (
        <option disabled hidden value="">
          {placeholder}
        </option>
      )}
      <SelectOptions items={options} supportsBaseSelect={isCustom} />
    </select>
  )
})

SelectNative.displayName = "SelectNative"
